import axios from "axios";
import { API_BASE_URL } from "./constants";
import toast from "react-hot-toast";

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

instance.interceptors.request.use(
  (config: any) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// intercept request body and if any field with empty string is found, set it to null
instance.interceptors.request.use(
  (config: any) => {
    if (config.data) {
      const data = config.data;
      Object.keys(data).forEach((key) => {
        if (data[key] === "" || data[key] === undefined) {
          data[key] = null;
        }
      });
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Silent access-token refresh ──────────────────────────────────────────────
// A 401 used to log the user straight out, even though the backend issues
// rotating refresh tokens valid for 7 days. Anyone who stayed on a page past
// the 1-hour access-token lifetime was kicked to the login screen mid-task
// (Maurice, Aug 2026). Now we spend the refresh token first and only log out
// if that also fails.
//
// Concurrent 401s share a single refresh: the first one performs it, the rest
// queue on the same promise and retry once it resolves, so a dashboard firing
// six parallel requests doesn't burn six refresh tokens (rotation would
// invalidate all but one and log the user out anyway).
let refreshInFlight: Promise<string> | null = null;

const performRefresh = async (): Promise<string> => {
  const refresh = localStorage.getItem("refreshToken");
  if (!refresh) throw new Error("no refresh token");
  // Bare axios, not `instance` — otherwise a failing refresh re-enters this
  // interceptor and loops.
  const { data } = await axios.post(`${API_BASE_URL}auth/token/refresh/`, { refresh });
  const nextAccess = data?.access;
  if (!nextAccess) throw new Error("refresh returned no access token");
  localStorage.setItem("accessToken", nextAccess);
  // Rotation is enabled server-side, so a new refresh token comes back too.
  if (data?.refresh) localStorage.setItem("refreshToken", data.refresh);
  return nextAccess;
};

const forceLogout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.location.href = "/login";
};

// interceptor response and if error is 401, try to refresh before logging out
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // A network failure has no response object; don't dereference it.
    if (!error.response) return Promise.reject(error);

    const original: any = error.config || {};
    const isRefreshCall = (original.url || "").includes("auth/token/refresh");

    if (error.response.status === 401 && !original._retried && !isRefreshCall) {
      original._retried = true;
      try {
        refreshInFlight = refreshInFlight || performRefresh().finally(() => {
          refreshInFlight = null;
        });
        const token = await refreshInFlight;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` };
        return instance(original);   // replay the request the user actually made
      } catch {
        forceLogout();
        return Promise.reject(error);
      }
    }

    if (error.response.status === 401) {
      forceLogout();
    }

    // if status is in range of internal server error, log error using toast
    if (error.response.status >= 500) {
      toast.error(error.response.statusText);
    }

    // if action is forbidden with code 403, log error using toast
    if (error.response.status === 403) {
      toast.error(error.response.statusText);
      window.location.href = "/forbidden";
    }
    return Promise.reject(error);
  }
);

export default instance;
